import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '@/atomic/atoms';
import { SCUICustomDataGrid } from '@/atomic/molecules';
import { downloadExcel } from '@/utils/excelFileHelper';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { EditDrawer } from '../components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Swal from 'sweetalert2';
import MPPaymentOnDeliveryCommissionCommand from '@/services/command/mpPaymentOnDeliveryCommission/MPPaymentOnDeliveryCommission.services';
import TimeHelper from '@/utils/time';
import { MPODC } from '../mpPaymentOnDeliveryCommission.classes';

export const CommissionList = ({
    rows,
    setIsLoading,
    setRows,
    fetchList,
    companyOptions,
    companyName,
    pagination,
    onPageChange,
    onPageSizeChange,
    filterCompanyId,
}: {
    rows: any;
    setIsLoading: (isLoading: boolean) => void;
    setRows: (rows: any) => void;
    fetchList: () => void;
    companyOptions: any[];
    companyName: string;
    pagination: { pageSize: number; pageNumber: number; totalCount: number };
    onPageChange: (pageZeroBased: number) => void;
    onPageSizeChange: (newPageSize: number) => void;
    filterCompanyId: number;
}) => {
    const { t } = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [formState, setFormState] = useState<any | null>(null);

    const initialFormState = () => ({
        id: 0,
        shippingCompanyId: filterCompanyId || 0,
        shippingCompanyName: '',
        minimumPrice: 0,
        maximumPrice: 0,
        commission: 0,
    });

    const resetForm = () => {
        setFormState(initialFormState());
    };

    const handleOpenAdd = () => {
        setIsEditMode(false);
        resetForm();
        setDrawerOpen(true);
    };

    const handleOpenEdit = (row: any) => {
        setIsEditMode(true);
        setFormState({ ...row, shippingCompanyId: filterCompanyId || 0 });
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        resetForm();
    };

    const handleExportExcel = () => {
        const excelRows = (rows || []).map((r: any) => ({
            shippingCompanyName: companyName || '-',
            minimumPrice: r.minimumPrice,
            maximumPrice: r.maximumPrice,
            commission: r.commission,
            startDate: TimeHelper.formatDate(r.startDate),
            expirationDate: TimeHelper.formatDate(r.expirationDate),
            changedAt: TimeHelper.formatDate(r.lastModifiedDate),
            changedBy: r.lastModifiedByName,
        }));

        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const yyyy = now.getFullYear();
        const fileName = `MPKapıdaÖdemeKomisyonYönetimi_${dd}.${mm}.${yyyy}.xlsx`;

        downloadExcel({
            excelDownloadData: excelRows,
            excelDownloadFolderName: 'MP-Payment-On-Delivery-Commission',
            fileNameOverride: fileName,
        });
    };

    const handleDelete = (row: any) => {
        Swal.fire({
            title: t('general.delete'),
            text: t('general.areYouSure'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('general.delete'),
            cancelButtonText: t('general.cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                const payload = {
                    shippingCompanyId: Number(row.shippingCompanyId),
                    commission: Number(row.commission),
                    maximumPrice: Number(row.maximumPrice),
                    minimumPrice: Number(row.minimumPrice),
                    startDate: row.startDate,
                    expirationDate: row.expirationDate,
                };
                MPPaymentOnDeliveryCommissionCommand.DeleteCodCommissionRanges(payload)
                    .then(() => {
                        Swal.fire({
                            title: t('general.success'),
                            icon: 'success',
                            showConfirmButton: false,
                            timer: 1500,
                        });
                        fetchList();
                    })
                    .catch(() => {
                        Swal.fire({
                            title: t('general.error'),
                            text: t('general.anErrorOccured'),
                            icon: 'error',
                        });
                    });
            }
        });
    };

    const columns = [
        {
            field: 'id',
            headerName: t('mpPaymentOnDeliveryCommission.list.columns.id'),
            flex: 0.5,
            hide: true,
        },
        {
            field: 'shippingCompanyName',
            headerName: t('mpPaymentOnDeliveryCommission.list.columns.cargoCompany'),
            flex: 1,
            renderCell: () => <Typography>{companyName || '-'}</Typography>,
        },
        {
            field: 'minimumPrice',
            headerName: t('mpPaymentOnDeliveryCommission.list.columns.minimumPrice'),
            flex: 1,
            renderCell: (params: any) => <Typography>{`${params.row.minimumPrice} ₺`}</Typography>,
        },
        {
            field: 'maximumPrice',
            headerName: t('mpPaymentOnDeliveryCommission.list.columns.maximumPrice'),
            flex: 0.7,
            renderCell: (params: any) => <Typography>{`${params.row.maximumPrice} ₺`}</Typography>,
        },

        {
            field: 'commission',
            headerName: t('mpPaymentOnDeliveryCommission.list.columns.commission'),
            flex: 0.7,
            renderCell: (params: any) => <Typography>{`${params.row.commission} ₺`}</Typography>,
        },

        {
            field: 'startDate',
            headerName: t('mpCargoFeeManagement.list.columns.startDate'),
            flex: 1,
            renderCell: (params: any) => <Typography>{TimeHelper.formatDate(params.row.startDate)}</Typography>,
        },
        {
            field: 'expirationDate',
            headerName: t('mpCargoFeeManagement.list.columns.expirationDate'),
            flex: 1,
            renderCell: (params: any) => <Typography>{TimeHelper.formatDate(params.row.expirationDate)}</Typography>,
        },
        {
            field: 'lastModifiedDate',
            headerName: t('mpCargoFeeManagement.list.columns.changedAt'),
            flex: 1,
            renderCell: (params: any) => <Typography>{TimeHelper.formatDate(params.row.lastModifiedDate)}</Typography>,
        },
        {
            field: 'lastModifiedByName',
            headerName: t('mpCargoFeeManagement.list.columns.changedBy'),
            flex: 1,
        },
        {
            field: 'actions',
            headerName: t('general.actions'),
            flex: 0.8,
            renderCell: (params: any) => (
                <Box className={MPODC.datagridActionsCell} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                        className={MPODC.inputButtonRowEdit}
                        onClick={() => handleOpenEdit(params.row)}
                        sx={{
                            minWidth: '30px !important',
                            width: '30px !important',
                            minHeight: '30px !important',
                            height: '30px !important',
                            backgroundColor: '#00B8F4',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'none',
                            p: 0,
                            m: 0,
                            '&:hover': {
                                backgroundColor: '#0099c8',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <EditIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Button>
                    <Button
                        className={MPODC.inputButtonRowDelete}
                        onClick={() => handleDelete(params.row)}
                        sx={{
                            minWidth: '30px !important',
                            width: '30px !important',
                            minHeight: '30px !important',
                            height: '30px !important',
                            backgroundColor: 'red',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'none',
                            p: 0,
                            m: 0,
                            marginLeft: '8px',
                            '&:hover': {
                                backgroundColor: 'red',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <DeleteIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <Box className={MPODC.commissionListRoot} sx={{ width: '100%' }}>
            <Box className={MPODC.commissionListToolbarBack} sx={{ display: 'flex', justifyContent: 'flex-start', gap: '8px', marginBottom: '12px' }}>
                <SCUIButton
                    className={MPODC.inputButtonCommissionListBack}
                    text={t('general.back')}
                    type="secondary"
                    sizeType="fixed"
                    style={{ height: '35px' }}
                    size={'m'}
                    buttonType="button"
                    onClick={() => setRows(null)}
                    showStartIcon
                    startIcon={<ArrowBackIcon sx={{ color: '#0d6efd' }} />}
                />
            </Box>
            <Box className={MPODC.commissionListToolbarActions} sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}>
                <SCUIButton
                    className={MPODC.inputButtonCommissionListAdd}
                    text={t('mpPaymentOnDeliveryCommission.list.addNew')}
                    type="primary"
                    sizeType="fixed"
                    style={{ height: '35px' }}
                    size={'m'}
                    buttonType="button"
                    onClick={handleOpenAdd}
                    showStartIcon
                    startIcon={<AddIcon sx={{ color: '#fff' }} />}
                />
                <SCUIButton
                    className={MPODC.inputButtonCommissionListExport}
                    text={t('general.exportToExcel')}
                    type="secondary"
                    sizeType="fixed"
                    style={{ height: '35px' }}
                    size={'m'}
                    buttonType="button"
                    onClick={handleExportExcel}
                    showStartIcon
                    startIcon={<FileDownloadIcon sx={{ color: '#0d6efd' }} />}
                />
            </Box>
            <Box className={MPODC.commissionListGridWrap}>
                <SCUICustomDataGrid
                    rows={rows || []}
                    columns={columns}
                    paginationModeChange
                    rowCount={pagination.totalCount}
                    page={Math.max(0, pagination.pageNumber - 1)}
                    pageSize={pagination.pageSize}
                    onPageChange={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                />
            </Box>
            <EditDrawer
                open={drawerOpen}
                onClose={handleCloseDrawer}
                companyOptions={companyOptions || []}
                filterCompanyId={filterCompanyId}
                initialData={isEditMode ? (formState as any) : undefined}
                setIsLoading={setIsLoading}
                onSaved={fetchList}
            />
        </Box>
    );
};
